import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Container,
  Paper,
  Stepper,
  Stack,
  Title,
  Text,
  TextInput,
  Select,
  Button,
  Group,
  Alert,
  Loader,
  Center,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import {
  useMemberMeService,
  useMaritalStatusService,
} from '@actschurch/ccms-sdk';

/**
 * Onboarding wizard — runs the first time a confirmed user signs in. Required
 * before they can browse the rest of the app (gated by RequireProfileComplete).
 *
 * Steps:
 *   1. Basics (Surname, DateOfBirth, Gender)
 *   2. Marital + contact (MaritalStatus, AltContact, Telephone)
 *   3. Review + submit
 */
export default function OnboardingPage() {
  const navigate = useNavigate();
  const memberMeService = useMemberMeService();
  const maritalStatusService = useMaritalStatusService();

  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState<Date | null>(null);
  const [gender, setGender] = useState<string | null>(null);
  const [maritalStatusID, setMaritalStatusID] = useState<string | null>(null);
  const [cellNumber, setCellNumber] = useState('');
  const [altContact, setAltContact] = useState('');
  const [telephone, setTelephone] = useState('');

  const { data: member, isLoading: loadingMember } = useQuery({
    queryKey: ['members', 'me'],
    queryFn: () => memberMeService.getMe(),
  });

  const { data: maritalStatuses = [] } = useQuery({
    queryKey: ['lookup', 'marital-status'],
    queryFn: () => maritalStatusService.getAll(),
  });

  // Pre-fill form once member data arrives.
  useEffect(() => {
    if (!member) return;
    setFirstName(member.firstName ?? '');
    setLastName(member.lastName ?? '');
    if (member.dateOfBirth) {
      try { setDob(new Date(member.dateOfBirth as unknown as string)); } catch { /* */ }
    }
    setGender(member.gender ?? null);
    setMaritalStatusID(member.maritalStatusID ? String(member.maritalStatusID) : null);
    setCellNumber(member.cellNumber ?? '');
    setAltContact(member.altContact ?? '');
    setTelephone(member.telephone ?? '');
  }, [member]);

  const saveMutation = useMutation({
    mutationFn: () =>
      memberMeService.updateMe({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        dateOfBirth: dob ? dob.toISOString() : null,
        gender: gender ?? undefined,
        maritalStatusID: maritalStatusID ? Number(maritalStatusID) : undefined,
        cellNumber: cellNumber.trim(),
        altContact: altContact.trim() || undefined,
        telephone: telephone.trim() || undefined,
      }),
    onSuccess: () => navigate('/', { replace: true }),
    onError: (err: any) => setError(err?.message || 'Could not save profile'),
  });

  const validateStep = (idx: number): boolean => {
    setError(null);
    if (idx === 0) {
      if (!firstName.trim() || !lastName.trim() || !dob || !gender) {
        setError('Please fill in first name, surname, date of birth, and gender.');
        return false;
      }
    }
    if (idx === 1) {
      if (!maritalStatusID || !cellNumber.trim()) {
        setError('Marital status and cell number are required.');
        return false;
      }
    }
    return true;
  };

  const goNext = () => {
    if (validateStep(step)) setStep((s) => s + 1);
  };
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const submit = () => {
    if (validateStep(0) && validateStep(1)) saveMutation.mutate();
  };

  if (loadingMember) {
    return (
      <Container size={520} my={60}>
        <Center py="xl"><Loader /></Center>
      </Container>
    );
  }

  return (
    <Container size={620} my={40}>
      <Title order={2} ta="center" mb="xs">
        Complete your profile
      </Title>
      <Text c="dimmed" size="sm" ta="center" mb="lg">
        We need a few more details before you can browse the Resource Centre.
      </Text>

      <Paper withBorder shadow="sm" p="lg" radius="md">
        <Stepper active={step} onStepClick={setStep} allowNextStepsSelect={false}>
          <Stepper.Step label="Basics" description="Name & DOB">
            <Stack gap="md" mt="lg">
              <TextInput
                label="First name"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.currentTarget.value)}
              />
              <TextInput
                label="Surname"
                required
                value={lastName}
                onChange={(e) => setLastName(e.currentTarget.value)}
              />
              <DateInput
                label="Date of birth"
                required
                value={dob}
                onChange={(value) => setDob(value as Date | null)}
                clearable
                maxDate={new Date()}
              />
              <Select
                label="Gender"
                required
                data={[
                  { value: 'Male', label: 'Male' },
                  { value: 'Female', label: 'Female' },
                  { value: 'Other', label: 'Other' },
                  { value: 'PreferNotToSay', label: 'Prefer not to say' },
                ]}
                value={gender}
                onChange={setGender}
              />
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Contacts" description="Marital + phone">
            <Stack gap="md" mt="lg">
              <Select
                label="Marital status"
                required
                data={maritalStatuses.map((s: any) => ({
                  value: String(s.maritalStatusID ?? s.id),
                  label: s.name ?? '—',
                }))}
                value={maritalStatusID}
                onChange={setMaritalStatusID}
              />
              <TextInput
                label="Cellphone"
                required
                placeholder="+27..."
                value={cellNumber}
                onChange={(e) => setCellNumber(e.currentTarget.value)}
              />
              <TextInput
                label="Alternate contact"
                value={altContact}
                onChange={(e) => setAltContact(e.currentTarget.value)}
              />
              <TextInput
                label="Telephone"
                value={telephone}
                onChange={(e) => setTelephone(e.currentTarget.value)}
              />
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Review" description="Confirm + finish">
            <Stack gap="xs" mt="lg">
              <Text size="sm">
                <strong>Name:</strong> {firstName} {lastName}
              </Text>
              <Text size="sm">
                <strong>Date of birth:</strong> {dob ? dob.toLocaleDateString() : '—'}
              </Text>
              <Text size="sm">
                <strong>Gender:</strong> {gender ?? '—'}
              </Text>
              <Text size="sm">
                <strong>Marital status:</strong>{' '}
                {maritalStatuses.find((s: any) => String(s.maritalStatusID ?? s.id) === maritalStatusID)?.name ?? '—'}
              </Text>
              <Text size="sm"><strong>Cellphone:</strong> {cellNumber || '—'}</Text>
              {altContact && <Text size="sm"><strong>Alternate:</strong> {altContact}</Text>}
              {telephone && <Text size="sm"><strong>Telephone:</strong> {telephone}</Text>}
            </Stack>
          </Stepper.Step>
        </Stepper>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" mt="md">
            {error}
          </Alert>
        )}

        <Group justify="space-between" mt="xl">
          <Button variant="default" onClick={goBack} disabled={step === 0}>
            Back
          </Button>
          {step < 2 ? (
            <Button onClick={goNext}>Next</Button>
          ) : (
            <Button onClick={submit} loading={saveMutation.isPending} leftSection={<IconCheck size={16} />}>
              Finish
            </Button>
          )}
        </Group>
      </Paper>
    </Container>
  );
}
